import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  FiX, 
  FiUploadCloud, 
  FiPaperclip, 
  FiTrash2, 
  FiCalendar, 
  FiUserCheck,
  FiChevronDown,
  FiSearch,
  FiPlus,
  FiUser,
  FiUsers,
  } from 'react-icons/fi';
import toast from 'react-hot-toast';
import tasksApi, { canEditTask, canChangeTaskStatus } from '../../api/tasksApi';
import TaskChecklistIcon from '../shared/TaskChecklistIcon';

const PRIORITIES = [
  { 
    id: 'LOW', 
    label: 'Low', 
    base: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
    active: 'bg-slate-100 border-slate-300 text-slate-800 font-semibold ring-1 ring-slate-300'
  },
  { 
    id: 'MEDIUM', 
    label: 'Medium', 
    base: 'bg-blue-50/40 text-blue-700 border-blue-200 hover:bg-blue-50',
    active: 'bg-blue-100/70 border-blue-400 text-blue-900 font-semibold ring-1 ring-blue-400/30'
  },
  { 
    id: 'HIGH', 
    label: 'High', 
    base: 'bg-amber-50/40 text-amber-700 border-amber-200 hover:bg-amber-50',
    active: 'bg-amber-100/70 border-amber-400 text-amber-900 font-semibold ring-1 ring-amber-400/30'
  },
  { 
    id: 'CRITICAL', 
    label: 'Critical', 
    base: 'bg-rose-50/40 text-rose-700 border-rose-200 hover:bg-rose-50',
    active: 'bg-rose-100/70 border-rose-400 text-rose-900 font-semibold ring-1 ring-rose-400/30'
  }
];

const STATUSES = [
  { 
    id: 'TO_DO', 
    label: 'To Do', 
    dot: 'bg-slate-400',
    base: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
    active: 'bg-slate-100 border-slate-300 text-slate-800 font-semibold ring-1 ring-slate-300'
  },
  { 
    id: 'IN_PROGRESS', 
    label: 'In Progress', 
    dot: 'bg-blue-500',
    base: 'bg-blue-50/40 text-blue-700 border-blue-200 hover:bg-blue-50',
    active: 'bg-blue-100/70 border-blue-400 text-blue-900 font-semibold ring-1 ring-blue-400/30'
  },
  { 
    id: 'BLOCKED', 
    label: 'Blocked', 
    dot: 'bg-rose-500',
    base: 'bg-rose-50/40 text-rose-700 border-rose-200 hover:bg-rose-50',
    active: 'bg-rose-100/70 border-rose-400 text-rose-900 font-semibold ring-1 ring-rose-400/30'
  },
  { 
    id: 'COMPLETED', 
    label: 'Completed', 
    dot: 'bg-emerald-500',
    base: 'bg-emerald-50/40 text-emerald-700 border-emerald-200 hover:bg-emerald-50',
    active: 'bg-emerald-100/70 border-emerald-400 text-emerald-900 font-semibold ring-1 ring-emerald-400/30'
  }
];

export default function CreateTaskModal({ 
  isOpen, 
  onClose, 
  onSave, 
  users = [], 
  defaultProjectId = null,
  defaultProductId = null,
  initialData = null,
  isEdit = false
}) {
  const me = localStorage.getItem('username') || '';
  const userRole = (localStorage.getItem('role') || '').toUpperCase();
  const todayStr = new Date().toISOString().split('T')[0];

  const getTomorrowStr = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  };

  const getEndOfWeekStr = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = (5 - day + 7) % 7 || 7;
    d.setDate(d.getDate() + diff);
    return d.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    username: me,
    copyTo: [],
    deadLine: todayStr,
    priority: 'MEDIUM',
    taskStatus: 'TO_DO',
    projectId: defaultProjectId || '',
    productId: defaultProductId || ''
  });

  const [files, setFiles] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [assigneeList, setAssigneeList] = useState(users);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [projectsList, setProjectsList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const assignedProjectsRef = useRef([]);

  // Fetch projects assigned to the current user
  const fetchAssignedProjects = useCallback(async () => {
    if (assignedProjectsRef.current && assignedProjectsRef.current.length > 0) {
      return assignedProjectsRef.current;
    }
    try {
      const res = await tasksApi.getProjects();
      const list = Array.isArray(res) ? res : [];
      assignedProjectsRef.current = list;
      setAssignedProjects(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  // Prefetch user's assigned projects on modal open
  useEffect(() => {
    if (isOpen) {
      fetchAssignedProjects();
    }
  }, [isOpen, fetchAssignedProjects]);

  // Custom Assignee Dropdown State
  const [isAssigneeOpen, setIsAssigneeOpen] = useState(false);
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const assigneeDropdownRef = useRef(null);

  // Custom Copy To Dropdown State
  const [isCopyToOpen, setIsCopyToOpen] = useState(false);
  const [copyToQuery, setCopyToQuery] = useState('');
  const copyToDropdownRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Click outside to close assignee dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(e.target)) {
        setIsAssigneeOpen(false);
      }
      if (copyToDropdownRef.current && !copyToDropdownRef.current.contains(e.target)) {
        setIsCopyToOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Single dedicated loader for task form context (products, cascaded projects, and product/project-specific assignees)
  const loadFormContext = useCallback(async (prodId, projId, preserveUsername = false) => {
    setLoadingAssignees(true);
    try {
      const [data, userProjs] = await Promise.all([
        tasksApi.getTaskFormContext(prodId || null, projId || null),
        fetchAssignedProjects()
      ]);

      if (data?.products) {
        setProductsList(data.products);
      }
      if (data?.projects) {
        const projs = data.projects;
        setProjectsList(projs);

        // Auto-select project based on product selection:
        // 1. If explicit projId requested (e.g. initialData or defaultProjectId), maintain it if valid
        // 2. If the product has only ONE project, auto-select that single project
        // 3. If multiple projects exist, check which project they are assigned to under this product;
        //    if they are assigned to only one project, auto-select that project.
        let targetProjId = '';

        if (projId && projs.some(p => String(p.id) === String(projId))) {
          targetProjId = String(projId);
        } else if (prodId && projs.length > 0) {
          if (projs.length === 1) {
            targetProjId = String(projs[0].id);
          } else if (projs.length > 1 && Array.isArray(userProjs) && userProjs.length > 0) {
            const assignedUnderProduct = projs.filter(p =>
              userProjs.some(ap => String(ap.id || ap.projectId) === String(p.id))
            );
            if (assignedUnderProduct.length === 1) {
              targetProjId = String(assignedUnderProduct[0].id);
            }
          }
        }

        if (targetProjId) {
          setFormData(prev => ({
            ...prev,
            projectId: targetProjId
          }));
        } else if (prodId && !projId) {
          // If a product is selected but no single project can be resolved, clear invalid previous project
          setFormData(prev => ({
            ...prev,
            projectId: prev.projectId && projs.some(p => String(p.id) === String(prev.projectId)) ? prev.projectId : ''
          }));
        }
      }
      if (data?.assignees) {
        setAssigneeList(data.assignees);
        if (!preserveUsername && (prodId || projId)) {
          setFormData(prev => {
            if (prev.username === me) return prev;
            const exists = data.assignees.some(u => u.username === prev.username);
            return exists ? prev : { ...prev, username: '' };
          });
        }
      }
    } catch (err) {
      console.warn('Failed to fetch task form context:', err);
    } finally {
      setLoadingAssignees(false);
    }
  }, [fetchAssignedProjects, me]);

  // Fetch form context on open using initial or default product & project
  useEffect(() => {
    if (!isOpen) return;
    const initialProdId = initialData?.productId || defaultProductId || '';
    const initialProjId = initialData?.projectId || defaultProjectId || '';
    loadFormContext(initialProdId ? Number(initialProdId) : null, initialProjId ? Number(initialProjId) : null, true);
  }, [isOpen, defaultProductId, defaultProjectId, initialData, loadFormContext]);

  // Handle product selection: cascade projects and product-scoped assignees
  const handleProductChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({
      ...prev,
      productId: val,
      projectId: '' // Reset project so only projects for this product can be selected
    }));
    loadFormContext(val ? Number(val) : null, null, false);
  };

  // Handle project selection: optionally align product if unselected
  const handleProjectChange = (e) => {
    const val = e.target.value;
    const selectedProj = projectsList.find(p => String(p.id) === String(val));
    const associatedProdId = selectedProj?.productId || selectedProj?.product?.id || '';

    if (val && associatedProdId && (!formData.productId || String(formData.productId) !== String(associatedProdId))) {
      setFormData(prev => ({
        ...prev,
        projectId: val,
        productId: associatedProdId
      }));
      loadFormContext(Number(associatedProdId), Number(val), true);
    } else if (val) {
      setFormData(prev => ({
        ...prev,
        projectId: val
      }));
      loadFormContext(formData.productId ? Number(formData.productId) : (associatedProdId ? Number(associatedProdId) : null), Number(val), true);
    } else {
      setFormData(prev => ({
        ...prev,
        projectId: ''
      }));
      loadFormContext(formData.productId ? Number(formData.productId) : null, null, true);
    }
  };

  // Synchronize form with initialData or defaults
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        let existingCopyTo = [];
        if (Array.isArray(initialData.copyTo)) {
          existingCopyTo = initialData.copyTo.map(c => (typeof c === 'string' ? c : c.username)).filter(Boolean);
        }
        setFormData({
          title: initialData.title || '',
          description: initialData.description || '',
          username: initialData.username || '',
          copyTo: existingCopyTo,
          deadLine: initialData.deadLine ? initialData.deadLine.split('T')[0] : '',
          priority: initialData.priority || 'MEDIUM',
          taskStatus: initialData.taskStatus || 'TO_DO',
          projectId: initialData.projectId || defaultProjectId || '',
          productId: initialData.productId || defaultProductId || ''
        });
      } else {
        setFormData({
          title: '',
          description: '',
          username: me,
          copyTo: [],
          deadLine: todayStr,
          priority: 'MEDIUM',
          taskStatus: 'TO_DO',
          projectId: defaultProjectId || '',
          productId: defaultProductId || ''
        });
      }
      setFiles([]);
      setSubtasks([]);
      setNewSubtaskTitle('');
      setIsAssigneeOpen(false);
      setAssigneeQuery('');
      setSubmitting(false);
    }
  }, [isOpen, initialData, me, defaultProjectId, defaultProductId]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAssignToMe = () => {
    setFormData(prev => {
      let updatedProjId = prev.projectId;
      if (!updatedProjId && prev.productId && projectsList.length > 0) {
        if (projectsList.length === 1) {
          updatedProjId = String(projectsList[0].id);
        } else if (assignedProjectsRef.current && assignedProjectsRef.current.length > 0) {
          const userAssigned = projectsList.filter(p =>
            assignedProjectsRef.current.some(ap => String(ap.id || ap.projectId) === String(p.id))
          );
          if (userAssigned.length === 1) {
            updatedProjId = String(userAssigned[0].id);
          }
        }
      }
      return { ...prev, username: me, projectId: updatedProjId };
    });
    setIsAssigneeOpen(false);
    toast.success('Assigned to yourself');
  };

  const toggleCopyToUser = (uname) => {
    setFormData(prev => {
      const current = prev.copyTo || [];
      if (current.includes(uname)) {
        return { ...prev, copyTo: current.filter(u => u !== uname) };
      } else {
        return { ...prev, copyTo: [...current, uname] };
      }
    });
  };

  const removeCopyToUser = (uname) => {
    setFormData(prev => ({
      ...prev,
      copyTo: (prev.copyTo || []).filter(u => u !== uname)
    }));
  };

  const handleAddSubtask = () => {
    const trimmed = newSubtaskTitle.trim();
    if (!trimmed) return;
    setSubtasks(prev => [...prev, { id: Date.now(), title: trimmed }]);
    setNewSubtaskTitle('');
  };

  const handleRemoveSubtask = (indexToRemove) => {
    setSubtasks(prev => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    if (isEdit && initialData) {
      if (initialData.taskStatus === 'COMPLETED' && !canChangeTaskStatus(initialData, me, userRole)) {
        toast.error('Only the task creator or admin can edit or reopen a completed task.');
        return;
      }
      if (!canEditTask(initialData, me)) {
        toast.error('Only the task assignee or creator can edit this task.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        taskStatus: formData.taskStatus || 'TO_DO',
        priority: formData.priority || 'MEDIUM',
        deadLine: formData.deadLine || null,
        projectId: formData.projectId ? Number(formData.projectId) : null,
        productId: formData.productId ? Number(formData.productId) : null,
        username: formData.username || null,
        copyTo: formData.copyTo || []
      };

      // Pass payload, attached files, and subtasks to parent handler
      await onSave(payload, files, subtasks);
    } catch (err) {
      console.error('Task save failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Ensure current user is always available so they can self-assign under all circumstances
  const effectiveAssigneeList = useMemo(() => {
    if (!me || assigneeList.some(u => u.username === me)) {
      return assigneeList;
    }
    return [{ id: 'self', username: me, fullName: `${me} (You)`, department: '' }, ...assigneeList];
  }, [assigneeList, me]);

  // Find currently selected assignee object
  const selectedAssignee = effectiveAssigneeList.find(u => u.username === formData.username);

  // Filtered assignees for dropdown search
  const filteredAssignees = effectiveAssigneeList.filter(u => {
    const q = assigneeQuery.toLowerCase();
    const name = (u.fullName || u.username || '').toLowerCase();
    const uname = (u.username || '').toLowerCase();
    const dept = (u.department || '').toLowerCase();
    return name.includes(q) || uname.includes(q) || dept.includes(q);
  });

  const filteredAssigneesForCopyTo = effectiveAssigneeList.filter(u => {
    const q = copyToQuery.toLowerCase();
    const name = (u.fullName || u.username || '').toLowerCase();
    const uname = (u.username || '').toLowerCase();
    const dept = (u.department || '').toLowerCase();
    return name.includes(q) || uname.includes(q) || dept.includes(q);
  });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-5 overflow-y-auto">
      
      {/* Semi-transparent Backdrop Overlay */}
      <div 
        onClick={onClose}
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs transition-opacity duration-300"
        aria-hidden="true"
      />

      {/* Centered Modal Card in Middle of Screen */}
      <div 
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-dialog-title"
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col z-10 border border-slate-200 overflow-hidden my-auto max-h-[92vh]"
      >
        <style>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: #cbd5e1;
            border-radius: 9999px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: #94a3b8;
          }
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #cbd5e1 transparent;
          }
        `}</style>
        
        {/* Header - Clean */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
          <div>
            <h2 id="modal-dialog-title" className="text-base font-bold text-slate-900 tracking-tight">
              {isEdit ? 'Edit Task' : 'New Task'}
            </h2>
          </div>
          <button 
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
            title="Close"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Form Body - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 custom-scrollbar">
            
            {/* Title */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Title <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                placeholder="What needs to be done?"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 transition-all shadow-xs"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Description
              </label>
              <textarea
                rows={3}
                placeholder="Add notes, context, or requirements..."
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 transition-all shadow-xs resize-y"
              />
            </div>

            {/* Row 1: Product & Project (Product on left, Project on right) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Product
                </label>
                <select
                  value={formData.productId}
                  onChange={handleProductChange}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs transition-all"
                >
                  <option value="">Select Product</option>
                  {productsList.map(prod => (
                    <option key={prod.id} value={prod.id}>
                      {prod.productname || prod.name || `Product #${prod.id}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Project
                </label>
                <select
                  value={formData.projectId}
                  onChange={handleProjectChange}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs transition-all"
                >
                  <option value="">Select Project</option>
                  {projectsList.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.projectName || p.name || `Project #${p.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: Assignee & Due Date (Assignee on left, Due Date on right) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              
              {/* Custom Assignee Dropdown (No emp id, Name + Subtext under name) */}
              <div className="relative" ref={assigneeDropdownRef}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Assignee
                  </label>
                  {me && formData.username !== me && (
                    <button
                      type="button"
                      onClick={handleAssignToMe}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                    >
                      <FiUserCheck className="text-xs" />
                      <span>Assign to Me</span>
                    </button>
                  )}
                </div>

                {/* Dropdown Trigger Button */}
                <button
                  type="button"
                  onClick={() => setIsAssigneeOpen(prev => !prev)}
                  disabled={loadingAssignees}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl flex items-center justify-between text-left cursor-pointer hover:border-slate-300 transition-all shadow-2xs min-h-[42px]"
                >
                  {selectedAssignee ? (
                    <div className="flex items-center gap-2.5 min-w-0 pr-1">
                      <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-300 text-slate-700 flex items-center justify-center font-semibold text-[10px] shrink-0">
                        {(selectedAssignee.fullName || selectedAssignee.username).slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-800 truncate">
                          {selectedAssignee.fullName || selectedAssignee.username}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate">
                          @{selectedAssignee.username}{selectedAssignee.department ? ` • ${selectedAssignee.department}` : ''}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-400 text-xs">
                      <FiUser className="text-sm" />
                      <span>-- Unassigned --</span>
                    </div>
                  )}

                  <FiChevronDown className={`text-slate-400 shrink-0 text-sm transition-transform ${isAssigneeOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Underlying hidden select for automated testing & accessibility */}
                <select
                  value={formData.username}
                  onChange={e => setFormData({ ...formData, username: e.target.value })}
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <option value="">-- Unassigned --</option>
                  {assigneeList.map(u => (
                    <option key={u.id || u.username} value={u.username}>
                      {u.fullName || u.username}
                    </option>
                  ))}
                </select>

                {/* Dropdown Menu (Opened) */}
                {isAssigneeOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in">
                    
                    {/* Search inside Assignees */}
                    <div className="p-2 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2">
                      <FiSearch className="text-slate-400 text-xs shrink-0 ml-1" />
                      <input
                        type="text"
                        placeholder="Search member..."
                        value={assigneeQuery}
                        onChange={e => setAssigneeQuery(e.target.value)}
                        className="w-full bg-transparent text-xs text-slate-800 placeholder:text-slate-400 outline-none"
                        autoFocus
                      />
                    </div>

                    {/* Options List */}
                    <div className="max-h-52 overflow-y-auto divide-y divide-slate-50">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, username: '' }));
                          setIsAssigneeOpen(false);
                        }}
                        className={`w-full px-3.5 py-2 text-left text-xs font-medium hover:bg-slate-50 transition-colors flex items-center gap-2 ${
                          !formData.username ? 'bg-blue-50/60 text-blue-700 font-semibold' : 'text-slate-500'
                        }`}
                      >
                        <FiUser className="text-xs" />
                        <span>-- Unassigned --</span>
                      </button>

                      {filteredAssignees.length === 0 ? (
                        <div className="p-3 text-center text-slate-400 text-xs">
                          {loadingAssignees ? 'Loading members...' : 'No team member found'}
                        </div>
                      ) : (
                        filteredAssignees.map(u => {
                          const isSelected = formData.username === u.username;
                          const name = u.fullName || u.username;

                          return (
                            <button
                              key={u.id || u.username}
                              type="button"
                              onClick={() => {
                                setFormData(prev => {
                                  let updatedProjId = prev.projectId;
                                  if (!updatedProjId && prev.productId && projectsList.length > 0) {
                                    if (projectsList.length === 1) {
                                      updatedProjId = String(projectsList[0].id);
                                    } else if (assignedProjectsRef.current && assignedProjectsRef.current.length > 0) {
                                      const userAssigned = projectsList.filter(p =>
                                        assignedProjectsRef.current.some(ap => String(ap.id || ap.projectId) === String(p.id))
                                      );
                                      if (userAssigned.length === 1) {
                                        updatedProjId = String(userAssigned[0].id);
                                      }
                                    }
                                  }
                                  return { ...prev, username: u.username, projectId: updatedProjId };
                                });
                                setIsAssigneeOpen(false);
                              }}
                              className={`w-full px-3.5 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2.5 cursor-pointer ${
                                isSelected ? 'bg-blue-50/60' : ''
                              }`}
                            >
                              <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center font-semibold text-[10px] shrink-0">
                                {name.slice(0, 2).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className={`text-xs font-semibold truncate ${isSelected ? 'text-blue-900' : 'text-slate-800'}`}>
                                  {name}
                                </div>
                                <div className="text-[10px] text-slate-400 truncate">
                                  @{u.username}{u.department ? ` • ${u.department}` : ''}{u.role ? ` (${u.role.replace('ROLE_', '')})` : ''}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>

                  </div>
                )}
              </div>

              {/* Deadline with Presets */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <FiCalendar className="text-slate-400" />
                    <span>Due Date</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, deadLine: todayStr }))}
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded-md border transition-all cursor-pointer ${
                        formData.deadLine === todayStr
                          ? 'bg-blue-50 text-blue-700 border-blue-300 font-bold'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, deadLine: getTomorrowStr() }))}
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded-md border transition-all cursor-pointer ${
                        formData.deadLine === getTomorrowStr()
                          ? 'bg-blue-50 text-blue-700 border-blue-300 font-bold'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      Tomorrow
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, deadLine: getEndOfWeekStr() }))}
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded-md border transition-all cursor-pointer ${
                        formData.deadLine === getEndOfWeekStr()
                          ? 'bg-blue-50 text-blue-700 border-blue-300 font-bold'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      End of Week
                    </button>
                  </div>
                </div>
                <input
                  type="date"
                  min={todayStr}
                  value={formData.deadLine}
                  onChange={e => setFormData({ ...formData, deadLine: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:outline-none focus:border-slate-400 transition-all cursor-pointer shadow-xs"
                />
              </div>

            </div>

            {/* Row 3: Priority & Copy To (Cc) in One Row */}
            <div className={`grid grid-cols-1 ${isEdit ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={e => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs transition-all min-h-[42px]"
                >
                  {PRIORITIES.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {isEdit && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                    Status
                  </label>
                  <select
                    value={formData.taskStatus}
                    onChange={e => setFormData({ ...formData, taskStatus: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-700 focus:outline-none focus:border-blue-400 cursor-pointer shadow-2xs transition-all min-h-[42px]"
                  >
                    {STATUSES.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Copy To (Cc) Multi-Select in the same row */}
              <div className="relative" ref={copyToDropdownRef}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <FiUsers className="text-slate-400" />
                    <span>Copy To (Cc)</span>
                    {formData.copyTo.length > 0 && (
                      <span className="px-1.5 py-0.2 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                        {formData.copyTo.length}
                      </span>
                    )}
                  </label>
                  {formData.copyTo.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, copyTo: [] }))}
                      className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Selected Chips + Dropdown Trigger Button */}
                <div 
                  onClick={() => setIsCopyToOpen(prev => !prev)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl flex items-center justify-between text-left cursor-pointer hover:border-slate-300 transition-all shadow-2xs min-h-[42px]"
                >
                  <div className="flex-1 flex flex-wrap gap-1.5 items-center min-w-0 pr-1">
                    {formData.copyTo.length === 0 ? (
                      <div className="flex items-center gap-2 text-slate-400 text-xs">
                        <FiUsers className="text-sm shrink-0" />
                        <span className="truncate">Select members to Cc...</span>
                      </div>
                    ) : (
                      formData.copyTo.map(uname => {
                        const userObj = assigneeList.find(u => u.username === uname);
                        const displayName = userObj?.fullName || uname;
                        return (
                          <span
                            key={uname}
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-blue-50 text-blue-800 border border-blue-200/80 text-xs font-medium"
                            onClick={e => e.stopPropagation()}
                          >
                            <div className="w-4 h-4 rounded-full bg-blue-200 text-blue-900 flex items-center justify-center font-bold text-[9px]">
                              {displayName.slice(0, 2).toUpperCase()}
                            </div>
                            <span className="truncate max-w-[80px]">{displayName}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeCopyToUser(uname);
                              }}
                              className="p-0.5 hover:text-rose-600 rounded cursor-pointer"
                            >
                              <FiX className="text-xs" />
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>
                  <FiChevronDown className={`text-slate-400 shrink-0 text-sm transition-transform ${isCopyToOpen ? 'rotate-180' : ''}`} />
                </div>

                {/* Copy To Dropdown Menu (Opened) */}
                {isCopyToOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden animate-fade-in">
                    
                    {/* Search inside Copy To */}
                    <div className="p-2 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2">
                      <FiSearch className="text-slate-400 text-xs shrink-0 ml-1" />
                      <input
                        type="text"
                        placeholder="Search colleague by name or department..."
                        value={copyToQuery}
                        onChange={e => setCopyToQuery(e.target.value)}
                        className="w-full bg-transparent text-xs text-slate-800 placeholder:text-slate-400 outline-none"
                        autoFocus
                      />
                    </div>

                    {/* Options List with Checkboxes */}
                    <div className="max-h-52 overflow-y-auto divide-y divide-slate-50">
                      {filteredAssigneesForCopyTo.length === 0 ? (
                        <div className="p-3 text-center text-slate-400 text-xs">
                          {loadingAssignees ? 'Loading team members...' : 'No colleague found'}
                        </div>
                      ) : (
                        filteredAssigneesForCopyTo.map(u => {
                          const isChecked = formData.copyTo.includes(u.username);
                          const isMainAssignee = formData.username === u.username;
                          const name = u.fullName || u.username;

                          return (
                            <button
                              key={u.id || u.username}
                              type="button"
                              onClick={() => toggleCopyToUser(u.username)}
                              className={`w-full px-3.5 py-2 text-left hover:bg-slate-50 transition-colors flex items-center justify-between cursor-pointer ${
                                isChecked ? 'bg-blue-50/40' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                <div className="w-7 h-7 rounded-full bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center font-semibold text-[10px] shrink-0">
                                  {name.slice(0, 2).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-slate-800 truncate flex items-center gap-1.5">
                                    <span>{name}</span>
                                    {isMainAssignee && (
                                      <span className="text-[10px] font-normal text-amber-600 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200">
                                        Assignee
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-slate-400 truncate">
                                    @{u.username}{u.department ? ` • ${u.department}` : ''}
                                  </div>
                                </div>
                              </div>

                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}} // handled by button
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none"
                              />
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SUBTASKS SECTION (Before Attachments) */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <TaskChecklistIcon size={14} className="text-slate-500" />
                  <span>Subtasks</span>
                  {subtasks.length > 0 && (
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      {subtasks.length}
                    </span>
                  )}
                </label>
              </div>

              {/* Input row to add subtask */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Add a subtask and press Enter..."
                  value={newSubtaskTitle}
                  onChange={e => setNewSubtaskTitle(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSubtask();
                    }
                  }}
                  className="flex-1 px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 transition-all shadow-2xs"
                />
                <button
                  type="button"
                  onClick={handleAddSubtask}
                  disabled={!newSubtaskTitle.trim()}
                  className="px-3.5 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/70 rounded-xl text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shrink-0 flex items-center gap-1"
                >
                  <FiPlus className="text-sm" />
                  <span>Add</span>
                </button>
              </div>

              {/* Subtasks List */}
              {subtasks.length > 0 && (
                <div className="space-y-1.5 pt-1 max-h-36 overflow-y-auto custom-scrollbar">
                  {subtasks.map((st, idx) => (
                    <div
                      key={st.id || idx}
                      className="flex items-center justify-between gap-2 p-2.5 bg-slate-50/80 border border-slate-200/70 rounded-xl text-xs"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                        <span className="text-slate-800 font-medium truncate">{st.title}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSubtask(idx)}
                        className="text-slate-400 hover:text-rose-600 transition-colors p-1 rounded-md hover:bg-rose-50 cursor-pointer shrink-0"
                        title="Remove subtask"
                      >
                        <FiTrash2 className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                Attachments
              </label>
              <div className="border border-dashed border-slate-200 rounded-xl p-3.5 text-center hover:border-slate-300 hover:bg-slate-50/50 transition-all bg-slate-50/30">
                <input
                  type="file"
                  multiple
                  id="task-file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <label 
                  htmlFor="task-file-upload" 
                  className="cursor-pointer flex flex-row items-center justify-center gap-2"
                >
                  <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center border border-slate-200">
                    <FiUploadCloud className="text-sm" />
                  </div>
                  <div className="text-left">
                    <span className="text-xs font-medium text-slate-700 block">
                      Click to attach files
                    </span>
                  </div>
                </label>
              </div>

              {/* Uploaded File List */}
              {files.length > 0 && (
                <div className="mt-2 space-y-1 max-h-24 overflow-y-auto custom-scrollbar">
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs">
                      <div className="flex items-center gap-2 truncate text-slate-700">
                        <FiPaperclip className="shrink-0 text-slate-400" />
                        <span className="truncate">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="text-slate-400 hover:text-rose-600 p-1 rounded transition-colors cursor-pointer"
                      >
                        <FiTrash2 className="text-xs" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* Pinned Bottom Actions Bar: Clean Blue, Never Thick Black */}
          <div className="px-6 py-4 border-t border-slate-200 bg-white flex items-center justify-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-xs sm:text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 text-xs sm:text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center gap-2 cursor-pointer shadow-xs"
            >
              {submitting ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>{isEdit ? 'Save Changes' : 'Create Task'}</span>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>,
    document.body
  );
}
